/**
 * Course Builder Edit Page - TICKET QWZMN4 + ZKPVN1 + JXWPR9
 * 
 * Pantalla de edición de curso con tabs:
 * - Tab General: Metadata del curso (QWZMN4)
 * - Tab Temario: Editor de módulos y actividades (ZKPVN1)
 * - Tab Publicación: Validación y publicar (JXWPR9)
 * 
 * Features:
 * - Cargar curso desde API
 * - Editar metadata y temario
 * - Asignar agentes VRT y labs EPL (JXWPR9)
 * - Publicar con validación estricta (JXWPR9)
 * - Guardar cambios (PUT)
 * - Estados: loading, error, success
 */

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, 
  Save, 
  Loader,
  AlertCircle,
  CheckCircle,
  BookOpen,
  FileText,
  Eye,
  Send
} from 'lucide-react';
import { getCourseById, updateCourse } from '../../services/adminCoursesApi';
import { useAuth } from '../../context/AuthContext';
import ModulesEditor from '../../components/course_builder/ModulesEditor';
import TabPublicacion from '../../components/course_builder/TabPublicacion';

const CourseBuilderEditPage = () => {
  const { courseId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  
  // Estados principales
  const [course, setCourse] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);

  // Estados del formulario
  const [formData, setFormData] = useState({
    title: '',
    shortDescription: '',
    estimatedHours: 0,
    skills: ''
  });
  const [formError, setFormError] = useState(null);

  // Tab activo (General y Temario funcionan)
  const [activeTab, setActiveTab] = useState('general');

  // Verificar permisos
  useEffect(() => {
    if (user && user.role !== 'admin' && user.role !== 'teacher') {
      navigate('/dashboard');
    }
  }, [user, navigate]);

  // Cargar curso al montar
  useEffect(() => {
    loadCourse();
  }, [courseId]);

  const loadCourse = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const data = await getCourseById(courseId);
      setCourse(data);
      
      // Poblar formulario
      setFormData({
        title: data.title || '',
        shortDescription: data.shortDescription || '',
        estimatedHours: data.estimatedHours || 0,
        skills: Array.isArray(data.skills) ? data.skills.join(', ') : ''
      });
    } catch (err) {
      console.error('Error cargando curso:', err);
      
      if (err.message === 'Curso no encontrado') {
        setError('not_found');
      } else {
        setError('load_failed');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setFormError(null);
    setSaving(true);
    setSaveSuccess(false);

    try {
      // Parsear skills (separadas por coma)
      const skillsArray = formData.skills
        .split(',')
        .map(s => s.trim())
        .filter(s => s.length > 0);

      const updates = {
        title: formData.title.trim(),
        shortDescription: formData.shortDescription.trim(),
        estimatedHours: parseInt(formData.estimatedHours, 10),
        skills: skillsArray
      };

      const updatedCourse = await updateCourse(courseId, updates);
      setCourse(updatedCourse);
      
      // Mostrar mensaje de éxito
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (err) {
      setFormError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      draft: 'bg-gray-100 text-gray-700 border-gray-300',
      published: 'bg-green-100 text-green-700 border-green-300',
      archived: 'bg-red-100 text-red-700 border-red-300'
    };

    const labels = {
      draft: 'Borrador',
      published: 'Publicado',
      archived: 'Archivado'
    };

    return (
      <span className={`px-3 py-1 rounded-md text-sm font-medium border ${styles[status]}`}>
        {labels[status]}
      </span>
    );
  };

  // Loading state
  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <Loader className="w-8 h-8 animate-spin text-blue-600 mx-auto mb-3" />
          <p className="text-gray-600">Cargando curso...</p>
        </div>
      </div>
    );
  }

  // Error: Curso no encontrado
  if (error === 'not_found') {
    return (
      <div className="p-6">
        <div className="max-w-2xl mx-auto">
          <div className="bg-white rounded-lg border border-gray-200 p-12 text-center">
            <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-gray-900 mb-2">
              Curso no encontrado
            </h2>
            <p className="text-gray-600 mb-6">
              No se encontró un curso con ID: <code className="bg-gray-100 px-2 py-1 rounded">{courseId}</code>
            </p>
            <button
              onClick={() => navigate('/admin/course-builder')}
              className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              Volver a lista
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Error: Fallo al cargar
  if (error === 'load_failed') {
    return (
      <div className="p-6">
        <div className="max-w-2xl mx-auto">
          <div className="bg-red-50 border border-red-200 rounded-lg p-6 flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <h3 className="font-semibold text-red-900 mb-1">Error al cargar</h3>
              <p className="text-red-700 text-sm mb-4">
                No se pudo cargar el curso. Intenta nuevamente.
              </p>
              <button
                onClick={loadCourse}
                className="text-sm text-red-700 hover:text-red-800 font-medium"
              >
                Reintentar
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Render principal
  return (
    <div className="p-6">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <button
            onClick={() => navigate('/admin/course-builder')}
            className="inline-flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-4"
          >
            <ArrowLeft className="w-4 h-4" />
            Volver a lista
          </button>
          
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900 mb-2">
                Editar curso
              </h1>
              <p className="text-gray-600 text-sm">
                ID: <code className="bg-gray-100 px-2 py-1 rounded text-xs">{course?.courseId}</code>
              </p>
            </div>
            {course && getStatusBadge(course.status)}
          </div>
        </div>

        {/* Success Message */}
        {saveSuccess && (
          <div className="mb-6 bg-green-50 border border-green-200 rounded-lg p-4 flex items-center gap-3">
            <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" />
            <p className="text-green-700 font-medium">Cambios guardados exitosamente</p>
          </div>
        )}

        {/* Tabs */}
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          <div className="border-b border-gray-200 flex">
            {/* Tab: General */}
            <button
              onClick={() => setActiveTab('general')}
              className={`flex items-center gap-2 px-6 py-3 text-sm font-medium transition-colors border-b-2 ${
                activeTab === 'general'
                  ? 'border-blue-600 text-blue-600 bg-blue-50'
                  : 'border-transparent text-gray-600 hover:text-gray-900 hover:bg-gray-50'
              }`}
            >
              <FileText className="w-4 h-4" />
              General
            </button>

            {/* Tab: Temario (habilitado en ZKPVN1) */}
            <button
              onClick={() => setActiveTab('temario')}
              className={`flex items-center gap-2 px-6 py-3 text-sm font-medium transition-colors border-b-2 ${
                activeTab === 'temario'
                  ? 'border-blue-600 text-blue-600 bg-blue-50'
                  : 'border-transparent text-gray-600 hover:text-gray-900 hover:bg-gray-50'
              }`}
            >
              <BookOpen className="w-4 h-4" />
              Temario
            </button>

            {/* Tab: Preview (disabled) */}
            <button
              disabled
              className="flex items-center gap-2 px-6 py-3 text-sm font-medium text-gray-400 cursor-not-allowed opacity-50"
            >
              <Eye className="w-4 h-4" />
              Preview
            </button>

            {/* Tab: Publicación (habilitado en JXWPR9) */}
            <button
              onClick={() => setActiveTab('publicacion')}
              className={`flex items-center gap-2 px-6 py-3 text-sm font-medium transition-colors border-b-2 ${
                activeTab === 'publicacion'
                  ? 'border-blue-600 text-blue-600 bg-blue-50'
                  : 'border-transparent text-gray-600 hover:text-gray-900 hover:bg-gray-50'
              }`}
            >
              <Send className="w-4 h-4" />
              Publicación
            </button>
          </div>

          {/* Tab Content: General */}
          {activeTab === 'general' && (
            <div className="p-6">
              <form onSubmit={handleSave} className="space-y-6">
                {/* Title */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Título del curso *
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Design Thinking"
                  />
                </div>

                {/* Short Description */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Descripción corta *
                  </label>
                  <textarea
                    required
                    value={formData.shortDescription}
                    onChange={(e) => setFormData({ ...formData, shortDescription: e.target.value })}
                    rows={4}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                    placeholder="Aprende metodologías ágiles para resolver problemas complejos..."
                  />
                </div>

                {/* Estimated Hours */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Horas estimadas *
                  </label>
                  <input
                    type="number"
                    required
                    min="0"
                    step="0.5"
                    value={formData.estimatedHours}
                    onChange={(e) => setFormData({ ...formData, estimatedHours: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                {/* Skills */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Skills
                  </label>
                  <input
                    type="text"
                    value={formData.skills}
                    onChange={(e) => setFormData({ ...formData, skills: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Empatía, Framing, Ideación"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Separadas por coma
                  </p>
                </div>

                {/* Error Message */}
                {formError && (
                  <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm flex items-start gap-2">
                    <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                    <span>{formError}</span>
                  </div>
                )}

                {/* Actions */}
                <div className="flex items-center justify-end gap-3 pt-4 border-t border-gray-200">
                  <button
                    type="button"
                    onClick={() => navigate('/admin/course-builder')}
                    className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                    disabled={saving}
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    disabled={saving}
                    className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
                  >
                    {saving ? (
                      <>
                        <Loader className="w-4 h-4 animate-spin" />
                        Guardando...
                      </>
                    ) : (
                      <>
                        <Save className="w-4 h-4" />
                        Guardar cambios
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          )}

          {/* Tab Content: Temario (ZKPVN1) */}
          {activeTab === 'temario' && (
            <div className="p-6">
              <ModulesEditor 
                course={course} 
                onCourseUpdate={(updatedCourse) => {
                  setCourse(updatedCourse);
                  // Recargar desde backend para asegurar consistencia
                  loadCourse();
                }}
              />
            </div>
          )}

          {/* Tab Content: Publicación (JXWPR9) */}
          {activeTab === 'publicacion' && (
            <div className="p-6">
              <TabPublicacion
                course={course}
                onCourseUpdate={(updatedCourse) => {
                  setCourse(updatedCourse);
                  // Recargar desde backend para asegurar consistencia
                  loadCourse();
                }}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CourseBuilderEditPage;
